<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
    @click.self="handleClose"
  >
    <div class="bg-white rounded-2xl w-full max-w-sm shadow-xl border border-gray-100 overflow-hidden">
      <div class="p-5">
        <h3 class="font-bold text-textMain text-base mb-2">{{ isEditMode ? '編輯接種紀錄' : '確認接種' }}</h3>
        <p class="text-xs text-textMuted leading-relaxed mb-4">
          請輸入「實際接種日期」。系統會以此日期重新推算同疫苗後續劑次（若有）。
        </p>

        <!-- 疫苗資訊 -->
        <div class="bg-gray-50 rounded-xl p-3 mb-4">
          <div class="text-sm font-bold text-textMain">
            {{ vaccineName }}
          </div>
          <div class="text-xs text-textMuted mt-1">
            {{ doseLabel }}
          </div>
        </div>

        <!-- 實際接種日期 -->
        <div class="mb-4">
          <label class="block text-xs font-bold text-textMain mb-2">實際接種日期</label>
          <MobileDatePicker
            v-model="localDate"
            :max="maxDate"
          />
        </div>

        <!-- 提前接種提示 -->
        <div v-if="showEarlyWarning" class="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <div class="flex items-start gap-2">
            <span class="text-amber-500 text-base leading-none flex-shrink-0">⚠️</span>
            <p class="text-xs text-amber-900 leading-relaxed">
              此接種日期比疾管署建議的最早可接種時間提前超過4天，依規定可能視同無效需補種，建議與醫師確認。
            </p>
          </div>
        </div>

        <!-- 輪狀病毒特殊選項 -->
        <div v-if="showRotaOptions" class="mt-4">
          <label class="block text-xs font-bold text-textMain mb-2">輪狀病毒劑型</label>
          <div class="flex gap-2">
            <button
              @click="localRotaType = '2'"
              class="flex-1 py-2 rounded-xl text-xs font-bold transition-all"
              :class="localRotaType === '2' ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
            >
              2劑型
            </button>
            <button
              @click="localRotaType = '3'"
              class="flex-1 py-2 rounded-xl text-xs font-bold transition-all"
              :class="localRotaType === '3' ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
            >
              3劑型
            </button>
          </div>
          <p class="text-[10px] text-textMuted mt-2">
            選擇 2劑型後，系統將自動跳過第3劑
          </p>
        </div>

        <!-- 流感特殊選項 -->
        <div v-if="showFluOptions" class="mt-4">
          <label class="flex items-start gap-2 cursor-pointer group">
            <input
              type="checkbox"
              v-model="localFluFirstTime"
              class="w-4 h-4 mt-0.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary"
            />
            <div>
              <span class="text-xs text-textMain font-medium group-hover:text-primary transition-colors">
                首次接種流感疫苗
              </span>
              <p class="text-[10px] text-textMuted mt-0.5">
                6個月～8歲幼童若首次接種，需間隔4週接種第2劑
              </p>
            </div>
          </label>
        </div>

        <!-- 日本腦炎疫苗提示 -->
        <div v-if="showJENotice" class="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <div class="flex items-start gap-2">
            <span class="text-amber-500 text-base leading-none flex-shrink-0">⚠️</span>
            <p class="text-xs text-amber-900 leading-relaxed">
              如幼兒經醫師評估改接種公費不活化日本腦炎疫苗，後續接種時間需另行向醫生詢問並建議自行記錄。
            </p>
          </div>
        </div>

        <!-- 按鈕區 -->
        <div class="flex gap-2 mt-5">
          <button
            class="flex-1 py-3 rounded-xl bg-gray-100 text-gray-600 font-bold text-sm hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="loading"
            @click="handleClose"
          >
            取消
          </button>
          <button
            class="flex-1 py-3 rounded-xl bg-primary text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors shadow-md"
            :disabled="!canConfirm || loading"
            @click="handleConfirm"
          >
            {{ loading ? '處理中...' : '確認' }}
          </button>
        </div>

        <!-- 提示文字 -->
        <p class="text-[11px] text-textMuted mt-4 leading-relaxed">
          💡 提醒：接種日期變更後，已加入行事曆的事件可能需要重新加入，以確保提醒正確。
        </p>

        <!-- 改回未接種（僅編輯模式顯示） -->
        <button
          v-if="isEditMode"
          class="w-full mt-4 pt-4 border-t border-gray-100 text-center text-xs font-bold text-red-500 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading"
          @click="handleUnconfirm"
        >
          改回未接種
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import MobileDatePicker from './MobileDatePicker.vue';
import type { VaccinationRecord } from '../types';

/* -------------------------
   Props & Emits
------------------------- */
interface Props {
  show: boolean;
  record: VaccinationRecord | null;
  vaccineName: string;
  doseLabel: string;
  earliestValidDate?: string | null; // YYYY-MM-DD，提前接種寬限期下限；null 表示此劑次不適用該規則
  loading?: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'confirm', data: {
    recordId: string;
    actualDate: string;
    options: {
      rotaType?: '2' | '3';
      fluFirstTime?: boolean;
    };
  }): void;
  (e: 'unconfirm', recordId: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

/* -------------------------
   Local State
------------------------- */
const localDate = ref('');
const localRotaType = ref<'2' | '3'>('3');
const localFluFirstTime = ref(true);

/* -------------------------
   Computed
------------------------- */
const isEditMode = computed(() => props.record?.status === 'completed');

const maxDate = computed(() => {
  return new Date().toISOString().split('T')[0] || '';
});

const showRotaOptions = computed(() => {
  return props.record?.vaccineId === 'rota' && props.record?.doseIndex === 1;
});

const showFluOptions = computed(() => {
  // 流感不再需要詢問是否首次接種，系統會自動判斷
  return false;
});

const showJENotice = computed(() => {
  return props.record?.vaccineId === 'je';
});

// 提前接種視同無效規則：實際接種日期早於寬限期下限時提示，僅供參考不阻擋確認
const showEarlyWarning = computed(() => {
  return !!props.earliestValidDate && !!localDate.value && localDate.value < props.earliestValidDate;
});

const canConfirm = computed(() => {
  return localDate.value && localDate.value.trim() !== '';
});

/* -------------------------
   Watchers
------------------------- */
watch(() => props.show, (newVal) => {
  if (newVal) {
    // Modal 打開時初始化：編輯模式預填實際接種日期，新增模式預設今天
    const today = new Date().toISOString().split('T')[0] || '';
    localDate.value = isEditMode.value ? (props.record?.actualDate ?? today) : today;
    localRotaType.value = '3';
    localFluFirstTime.value = true;
  }
});

/* -------------------------
   Methods
------------------------- */
const handleClose = () => {
  if (props.loading) return;
  emit('close');
};

const handleConfirm = () => {
  if (!props.record?.id || !canConfirm.value) return;

  emit('confirm', {
    recordId: props.record.id,
    actualDate: localDate.value,
    options: {
      rotaType: showRotaOptions.value ? localRotaType.value : undefined,
      fluFirstTime: showFluOptions.value ? localFluFirstTime.value : undefined,
    },
  });
};

const handleUnconfirm = () => {
  if (!props.record?.id) return;
  emit('unconfirm', props.record.id);
};
</script>

<style scoped>
/* 如果需要自定義動畫 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-in {
  animation: fadeIn 0.2s ease-out;
}
</style>